import type { TFunction } from 'i18next';
import { useMemo } from 'react';
import {
  faFaceViewfinder,
  faFileLines,
  faGears,
  faListMusic,
} from '@fortawesome/pro-light-svg-icons';
import {
  faFaceViewfinder as faFaceViewfinderSolid,
  faFileLines as faFileLinesSolid,
  faGears as faGearsSolid,
  faListMusic as faListMusicSolid,
} from '@fortawesome/pro-solid-svg-icons';
import { useTranslation } from 'react-i18next';

import type { IContainerProps } from '~/components/utils/Container';
import type { IFeaturePlanFeatures } from '~/lib/api/modules/Apps';
import type { INavigationRailSectionItem } from './NavigationRailSection';
import { useAppCurrentFeaturePlanQuery } from '~/lib/api/modules/Apps/hooks/useAppCurrentFeaturePlanQuery';
import { NavigationRailSection } from './NavigationRailSection';

export type IAppNavigationRailContentProps = IContainerProps & {
  appId: string;
};

type IGetItemsProps = {
  baseUrl: string;
  t: TFunction<'translation', undefined>;
  features?: IFeaturePlanFeatures;
};

const getItems = ({
  baseUrl,
  t,
  features,
}: IGetItemsProps): Array<INavigationRailSectionItem> => [
  {
    label: t('AppNavigationRailContent.content'),
    faIcon: faFileLines,
    faActiveIcon: faFileLinesSolid,
    href: `${baseUrl}/content`,
    matchPatterns: [
      `${baseUrl}`,
      `${baseUrl}/content/*`,
      `${baseUrl}/articles/*`,
      `${baseUrl}/links/*`,
      `${baseUrl}/categories/*`,
    ],
  },
  {
    label: t('AppNavigationRailContent.playlists'),
    faIcon: faListMusic,
    faActiveIcon: faListMusicSolid,
    href: `${baseUrl}/playlists`,
    matchPatterns: [`${baseUrl}/playlists/*`],
    proFeature: !features?.playlists,
  },
  {
    label: t('AppNavigationRailContent.moderation'),
    faIcon: faFaceViewfinder,
    faActiveIcon: faFaceViewfinderSolid,
    href: `${baseUrl}/moderation`,
  },
  {
    label: t('AppNavigationRailContent.settings'),
    faIcon: faGears,
    faActiveIcon: faGearsSolid,
    href: `${baseUrl}/settings`,
    matchPatterns: [`${baseUrl}/settings/*`],
  },
];

export const AppNavigationRailContent: React.FC<
  IAppNavigationRailContentProps
> = (props) => {
  const { appId } = props;
  const { t } = useTranslation();

  const currentFeaturePlanQuery = useAppCurrentFeaturePlanQuery({
    appId,
  });
  const features = currentFeaturePlanQuery.data?.features;
  const baseUrl = `/apps/${appId}`;
  const items = useMemo(
    () =>
      currentFeaturePlanQuery.isLoading
        ? undefined
        : getItems({ baseUrl, t, features }),
    [currentFeaturePlanQuery.isLoading, baseUrl, features, t],
  );

  return (
    <NavigationRailSection
      items={items}
      loading={currentFeaturePlanQuery.isLoading}
    />
  );
};
